import {
	Button,
	Group,
	Modal,
	Stack,
	Text,
	Textarea,
	TextInput,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type CommentDialogProps = {
	opened: boolean;
	title: string;
	confirmLabel: string;
	confirmColor?: string;
	loading?: boolean;
	onClose: () => void;
	onConfirm: (comment: string) => void;
};

export function CommentDialog({
	opened,
	title,
	confirmLabel,
	confirmColor = "red",
	loading,
	onClose,
	onConfirm,
}: CommentDialogProps) {
	const { t } = useTranslation();
	const [comment, setComment] = useState("");

	const close = () => {
		setComment("");
		onClose();
	};

	return (
		<Modal opened={opened} onClose={close} title={title} centered>
			<Stack>
				<Textarea
					label={t("verification.comment")}
					description={t("verification.commentHint")}
					minRows={4}
					maxLength={1000}
					value={comment}
					onChange={(event) => setComment(event.currentTarget.value)}
				/>
				<Group justify="flex-end">
					<Button variant="default" onClick={close}>
						{t("common.cancel")}
					</Button>
					<Button
						color={confirmColor}
						loading={loading}
						disabled={comment.trim().length === 0}
						onClick={() => onConfirm(comment.trim())}
					>
						{confirmLabel}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}

type ApproveDialogProps = {
	opened: boolean;
	loading?: boolean;
	onClose: () => void;
	onConfirm: (expiresAt?: string) => void;
};

export function ApproveDialog({
	opened,
	loading,
	onClose,
	onConfirm,
}: ApproveDialogProps) {
	const { t } = useTranslation();
	const [expiresAt, setExpiresAt] = useState("");

	const close = () => {
		setExpiresAt("");
		onClose();
	};

	return (
		<Modal
			opened={opened}
			onClose={close}
			title={t("verification.approveTitle")}
			centered
		>
			<Stack>
				<Text size="sm" c="dimmed">
					{t("verification.approveHint")}
				</Text>
				<TextInput
					label={t("verification.expiresAt")}
					placeholder="2027-12-31"
					value={expiresAt}
					onChange={(event) => setExpiresAt(event.currentTarget.value)}
				/>
				<Group justify="flex-end">
					<Button variant="default" onClick={close}>
						{t("common.cancel")}
					</Button>
					<Button
						color="green"
						loading={loading}
						onClick={() => onConfirm(expiresAt.trim() || undefined)}
					>
						{t("verification.approve")}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
